import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { AppActorProvider } from '@/components/AppActorProvider/AppActorProvider'
import AppLayout from '@/components/AppLayout/AppLayout'
import { AudioEngineProvider } from '@/components/AudioEngineProvider/AudioEngineProvider'
import { ImageFormatProvider } from '@/components/ImageFormatProvider/ImageFormatProvider'
import DevCommands from '@/dev/DevCommands'
import DevGrid from '@/dev/DevGrid'
import { i18nInitOptions } from '@/i18n'
import { ErrorBoundary } from '@/instrumentation'
import { Audience, LTD, Persona } from '@/state/AppConfig'

import { StatsigProvider } from './components/StatsigProvider/StatsigProvider'

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init(
    i18nInitOptions({
      lng: import.meta.env.VITE_I18N_LOCALE,
      fallbackLng: import.meta.env.VITE_I18N_FALLBACK_LOCALE,
    })
  )

export type AppProps = {
  locale?: string
  userId?: string
  backUrl?: string
  persona?: string
  relationship?: Audience
  interestedBy?: LTD
}

function App(defaultParams: AppProps) {
  const params = { ...defaultParams }

  const initialBackUrl = import.meta.env.VITE_INITIAL_BACK_URL ? import.meta.env.VITE_INITIAL_BACK_URL : null
  const initialPersona = import.meta.env.VITE_INITIAL_PERSONA ? (import.meta.env.VITE_INITIAL_PERSONA as Persona) : null
  const initialRelationship = import.meta.env.VITE_INITIAL_RELATIONSHIP
    ? (import.meta.env.VITE_INITIAL_RELATIONSHIP as Audience)
    : 'UNSPECIFIED'
  const initialInterestedBy = import.meta.env.VITE_INITIAL_INTERESTED_BY
    ? (import.meta.env.VITE_INITIAL_INTERESTED_BY as LTD)
    : 'ADHD'
  if (!params.persona && initialPersona) {
    params.persona = initialPersona
  }
  if (!params.relationship && initialRelationship) {
    params.relationship = initialRelationship
  }
  if (!params.interestedBy && initialInterestedBy) {
    params.interestedBy = initialInterestedBy
  }
  if (!params.userId) {
    params.userId = 'anon-user'
  }
  if (!params.backUrl && initialBackUrl) {
    params.backUrl = initialBackUrl
  }

  /// #if _DEBUG
  console.log('[TME] 🛠️ Debug mode enabled')
  /// #endif

  console.log('[App] params:', { provided: defaultParams, computed: params })

  const locale = params.locale || 'en'
  i18n.changeLanguage(locale)

  return (
    <ErrorBoundary fallback={<p>Something went wrong</p>}>
      <StatsigProvider>
        <AudioEngineProvider>
          <ImageFormatProvider>
            <AppActorProvider>
              <AppLayout {...params} />
              <DevCommands />
              <DevGrid />
            </AppActorProvider>
          </ImageFormatProvider>
        </AudioEngineProvider>
      </StatsigProvider>
    </ErrorBoundary>
  )
}

export default App
